/**
 * AI Function Editor Component
 * Comprehensive editor for Agent Function entities with Parameters sub-table
 * Tabs: Basic | Configuration | Implementation | Parameters
 */

import { LockIcon, BugIcon, LightbulbIcon, ClipboardIcon, ZapIcon } from '@bizfirst/common-react';
import React, { useState, useEffect } from 'react';
import { ModalPortal } from '../../Modals/ModalPortal';
import LookupSelect from '../../Modals/LookupSelect';
import { agentFunctionApiClient, agentFunctionKnowledgeBaseApiClient, AgentFunctionKnowledgeBase } from '@flow-studio/api';
import { lookupConfigs } from '../../../lookupConfigs';
import { lookupEventBus } from 'flow-studio-core';
import { useUIStore } from '@flow-studio/store';
import { JsonEditor } from '../../Shared/JsonEditor';
import { EntityGrid, ColumnDefinition } from '../../Shared/EntityGrid';
import { Loader } from '../../Shared/Loader';
import ParameterEditor from './ParameterEditor';
import KnowledgeBaseEditor from './KnowledgeBaseEditor';
import '../Agent/AgentEditor.css'; // Reuse agent editor styles
import './AIFunctionEditor.css';

export interface Parameter {
  ParameterID?: number;
  ParameterName: string;
  ParameterType: string;
  ParameterDescription: string;
  IsRequired: boolean;
  DefaultValue: string;
  DisplayOrder: number;
}

export interface AIFunctionFormData {
  AgentFunctionID?: number;
  ResID?: string;
  AgentID: number;
  Name: string;
  Description: string;
  Parameters: string; // JSON string
  McpToolID: number | null;
  FunctionTypeID: number | null;
  PrimaryConnectorGroupID: number | null;
  Assembly: string;
  ClassName: string;
  MethodName: string;
  IsActive?: boolean;
  CreatedOn?: string;
  CreatedBy?: string;
  LastModifiedOn?: string;
  LastModifiedBy?: string;
}

interface AIFunctionEditorProps {
  functionId?: number;
  agentId?: number;
  isOpen: boolean;
  onClose: () => void;
  onFunctionSaved?: (func: AIFunctionFormData) => void;
}

type FunctionTab = 'basic' | 'configuration' | 'implementation' | 'parameters' | 'knowledgeBases' | 'security' | 'debugger';

const defaultFormData: AIFunctionFormData = {
  AgentID: 0,
  Name: '',
  Description: '',
  Parameters: '{}',
  McpToolID: null,
  FunctionTypeID: null,
  PrimaryConnectorGroupID: null,
  Assembly: '',
  ClassName: '',
  MethodName: '',
  IsActive: true,
};

export const AIFunctionEditor: React.FC<AIFunctionEditorProps> = ({
  functionId,
  agentId,
  isOpen,
  onClose,
  onFunctionSaved,
}) => {
  const { showToast } = useUIStore();
  const [activeTab, setActiveTab] = useState<FunctionTab>('basic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [formData, setFormData] = useState<AIFunctionFormData>(defaultFormData);
  const [parameters, setParameters] = useState<Parameter[]>([]);
  const [parameterEditorOpen, setParameterEditorOpen] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState<Parameter | null>(null);
  const [knowledgeBases, setKnowledgeBases] = useState<AgentFunctionKnowledgeBase[]>([]);
  const [knowledgeBasesLoading, setKnowledgeBasesLoading] = useState(false);
  const [knowledgeBaseEditorOpen, setKnowledgeBaseEditorOpen] = useState(false);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState<AgentFunctionKnowledgeBase | null>(null);

  // Load Function on open
  useEffect(() => {
    if (isOpen) {
      if (functionId) {
        loadFunctionData();
        loadKnowledgeBases();
        setIsEditing(false);
      } else {
        resetForm();
        setIsEditing(true);
        if (agentId) {
          setFormData((prev) => ({ ...prev, AgentID: agentId }));
        }
      }
    }
  }, [isOpen, functionId, agentId]);

  const loadFunctionData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await agentFunctionApiClient.getById({ id: functionId! });
      if (response.success && response.data) {
        setFormData(response.data as unknown as AIFunctionFormData);
        // Parse Parameters JSON
        try {
          const params = JSON.parse((response.data as unknown as AIFunctionFormData).Parameters || '{}');
          if (Array.isArray(params)) {
            setParameters(params);
          }
        } catch {
          // Parameters are just JSON schema, not array of Parameter objects
          setParameters([]);
        }
      } else {
        setError(response.error?.message || 'Failed to load Function');
      }
    } catch (err: any) {
      setError(err.message || 'Error loading Function');
    } finally {
      setLoading(false);
    }
  };

  const loadKnowledgeBases = async () => {
    if (!functionId) return;
    setKnowledgeBasesLoading(true);
    try {
      const response = await agentFunctionKnowledgeBaseApiClient.getByAgentFunctionId(functionId);
      if (response.success && response.data) {
        setKnowledgeBases(response.data);
      } else {
        setError(response.error?.message || 'Failed to load Knowledge Bases');
      }
    } catch (err: any) {
      setError(err.message || 'Error loading Knowledge Bases');
    } finally {
      setKnowledgeBasesLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(defaultFormData);
    setParameters([]);
    setKnowledgeBases([]);
    setError(null);
    setIsDirty(false);
  };

  const handleRefresh = async () => {
    if (functionId) {
      await loadFunctionData();
      await loadKnowledgeBases();
    }
  };

  const handleChange = (field: keyof AIFunctionFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsDirty(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.Name.trim()) {
      setError('Function name is required');
      return;
    }

    if (!formData.AgentID) {
      setError('Agent ID is required');
      return;
    }

    setLoading(true);
    try {
      const response = functionId
        ? await agentFunctionApiClient.update(functionId, formData)
        : await agentFunctionApiClient.create(formData);

      if (response.success && response.data) {
        //  Auto-refresh related lookups
        if (!functionId) {
          lookupEventBus.emitMultiple([
            'lookup_agent',
            'lookup_functionType',
            'lookup_mcpTool',
            'lookup_connectorGroup',
          ]);
        }

        onFunctionSaved?.(response.data as unknown as AIFunctionFormData);
        onClose();
      } else {
        setError(response.error?.message || 'Failed to save Function');
      }
    } catch (err: any) {
      setError(err.message || 'Error saving Function');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      showToast('info', 'Changes discarded');
    }
    onClose();
  };

  const handleAddParameter = () => {
    setSelectedParameter(null);
    setParameterEditorOpen(true);
  };

  const handleEditParameter = (param: Parameter) => {
    setSelectedParameter(param);
    setParameterEditorOpen(true);
  };

  const handleDeleteParameter = (id: string | number) => {
    setParameters((prev) =>
      prev.filter((p) => p.ParameterID !== id)
    );
    setIsDirty(true);
  };

  const handleParameterSaved = (parameter: Parameter) => {
    if (selectedParameter?.ParameterID) {
      // Update existing
      setParameters((prev) =>
        prev.map((p) => (p.ParameterID === parameter.ParameterID ? parameter : p))
      );
    } else {
      // Add new
      setParameters((prev) => [
        ...prev,
        {
          ...parameter,
          ParameterID: Math.min(...parameters.map((p) => p.ParameterID || 0)) - 1,
        },
      ]);
    }
    setParameterEditorOpen(false);
    setSelectedParameter(null);
    setIsDirty(true);
  };

  const handleAddKnowledgeBase = () => {
    setSelectedKnowledgeBase(null);
    setKnowledgeBaseEditorOpen(true);
  };

  const handleEditKnowledgeBase = (kb: AgentFunctionKnowledgeBase) => {
    setSelectedKnowledgeBase(kb);
    setKnowledgeBaseEditorOpen(true);
  };

  const handleDeleteKnowledgeBase = async (id: string | number) => {
    try {
      const response = await agentFunctionKnowledgeBaseApiClient.deleteById(Number(id));
      if (response.success) {
        setKnowledgeBases((prev) =>
          prev.filter((kb) => kb.AgentFunctionKnowledgeBaseID !== id)
        );
      } else {
        setError(response.error?.message || 'Failed to delete Knowledge Base association');
      }
    } catch (err: any) {
      setError(err.message || 'Error deleting Knowledge Base association');
    }
  };

  const handleKnowledgeBaseSaved = (knowledgeBase: AgentFunctionKnowledgeBase) => {
    if (selectedKnowledgeBase?.AgentFunctionKnowledgeBaseID) {
      // Update existing
      setKnowledgeBases((prev) =>
        prev.map((kb) =>
          kb.AgentFunctionKnowledgeBaseID === selectedKnowledgeBase.AgentFunctionKnowledgeBaseID
            ? knowledgeBase
            : kb
        )
      );
    } else {
      // Add new
      setKnowledgeBases((prev) => [...prev, knowledgeBase]);
    }
    setKnowledgeBaseEditorOpen(false);
    setSelectedKnowledgeBase(null);
  };

  if (!isOpen) return null;

  const parameterColumns: ColumnDefinition[] = [
    {
      key: 'ParameterName',
      label: 'Name',
    },
    {
      key: 'ParameterType',
      label: 'Type',
    },
    {
      key: 'IsRequired',
      label: 'Required',
      render: (value) => (value ? '✓ Yes' : '○ No'),
    },
  ];

  const knowledgeBaseColumns: ColumnDefinition[] = [
    {
      key: 'Usage',
      label: 'Usage',
    },
    {
      key: 'Priority',
      label: 'Priority',
    },
    {
      key: 'Collection',
      label: 'Collection',
      render: (_value, entity) =>
        entity.DocumentCollectionID ? `#${entity.DocumentCollectionID}` : entity.CollectionNameTemplate || '—',
    },
    {
      key: 'IsActive',
      label: 'Active',
      render: (value) => (value ? '✓ Yes' : '○ No'),
    },
  ];

  return (
    <ModalPortal>
      <div className="agent-editor-overlay" onClick={handleCancel}>
        <div
          className="agent-editor-container"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="agent-editor-header">
            <h1 className="editor-title">
              {functionId ? 'Edit Function' : 'Create Function'}
            </h1>
            <div className="editor-header-actions">
              <button className="close-btn" onClick={handleCancel}>
                ✕
              </button>
            </div>
          </div>

          {/* Error Banner */}
          {error && (
            <div className="editor-error-banner">
              <span>{error}</span>
            </div>
          )}

          {/* Loading State */}
          {loading && !functionId && (
            <div className="editor-loading">
              <Loader label="Loading..." />
            </div>
          )}

          {/* Content */}
          {!loading && (
            <form onSubmit={handleSave} className="editor-form">
              {/* Tabs */}
              <div className="top-tabs">
                {(['basic', 'configuration', 'implementation', 'parameters', 'knowledgeBases', 'security', 'debugger'] as FunctionTab[]).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={`tab ${activeTab === tab ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab === 'basic' && 'Basic'}
                    {tab === 'configuration' && 'Configuration'}
                    {tab === 'implementation' && 'Implementation'}
                    {tab === 'parameters' && 'Parameters'}
                    {tab === 'knowledgeBases' && 'Knowledge Bases'}
                    {tab === 'security' && <><LockIcon size={12} /> Security</>}
                    {tab === 'debugger' && <><BugIcon size={12} /> Debugger</>}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="tab-content">
                {/* Basic Tab */}
                {activeTab === 'basic' && (
                  <div className="form-grid">
                    <div className="form-group full">
                      <label>Function Name *</label>
                      <input
                        type="text"
                        value={formData.Name}
                        onChange={(e) => handleChange('Name', e.target.value)}
                        disabled={!isEditing}
                        placeholder="e.g., SendEmail, ProcessPayment"
                        maxLength={200}
                      />
                    </div>

                    <div className="form-group">
                      <LookupSelect
                        fetchConfig={lookupConfigs.agent}
                        value={formData.AgentID}
                        onChange={(value) => handleChange('AgentID', value || 0)}
                        label="Agent"
                        required={true}
                        disabled={!isEditing || !!agentId}
                        placeholder="Select agent..."
                        searchable={true}
                        maxItemsInDropdown={50}
                      />
                    </div>

                    <div className="form-group full">
                      <label>Description</label>
                      <textarea
                        value={formData.Description}
                        onChange={(e) => handleChange('Description', e.target.value)}
                        disabled={!isEditing}
                        placeholder="Detailed description of what this function does"
                        rows={3}
                        maxLength={1000}
                      />
                    </div>
                  </div>
                )}

                {/* Configuration Tab */}
                {activeTab === 'configuration' && (
                  <div className="form-grid">
                    <div className="form-group">
                      <LookupSelect
                        fetchConfig={lookupConfigs.functionType}
                        value={formData.FunctionTypeID}
                        onChange={(value) => handleChange('FunctionTypeID', value)}
                        label="Function Type"
                        required={false}
                        disabled={!isEditing}
                        placeholder="Select function type (optional)..."
                        searchable={true}
                        maxItemsInDropdown={50}
                      />
                    </div>

                    <div className="form-group">
                      <LookupSelect
                        fetchConfig={lookupConfigs.mcpTool}
                        value={formData.McpToolID}
                        onChange={(value) => handleChange('McpToolID', value)}
                        label="MCP Tool"
                        required={false}
                        disabled={!isEditing}
                        placeholder="Select MCP tool (optional)..."
                        searchable={true}
                        maxItemsInDropdown={50}
                      />
                    </div>

                    <div className="form-group">
                      <LookupSelect
                        fetchConfig={lookupConfigs.connectorGroup}
                        value={formData.PrimaryConnectorGroupID}
                        onChange={(value) => handleChange('PrimaryConnectorGroupID', value)}
                        label="Primary Connector Group"
                        required={false}
                        disabled={!isEditing}
                        placeholder="Select connector group (optional)..."
                        searchable={true}
                        maxItemsInDropdown={50}
                      />
                    </div>

                    <div className="form-group full">
                      <JsonEditor
                        label="Parameters Schema (JSON)"
                        value={formData.Parameters}
                        onChange={(value) => handleChange('Parameters', value)}
                        disabled={!isEditing}
                        height="250px"
                        placeholder='{"property": "value"}'
                      />
                    </div>
                  </div>
                )}

                {/* Implementation Tab */}
                {activeTab === 'implementation' && (
                  <div className="form-grid">
                    <div className="form-group full">
                      <label>Assembly</label>
                      <input
                        type="text"
                        value={formData.Assembly}
                        onChange={(e) => handleChange('Assembly', e.target.value)}
                        disabled={!isEditing}
                        placeholder="e.g., MyAssembly.dll"
                        maxLength={500}
                      />
                    </div>

                    <div className="form-group full">
                      <label>Class Name</label>
                      <input
                        type="text"
                        value={formData.ClassName}
                        onChange={(e) => handleChange('ClassName', e.target.value)}
                        disabled={!isEditing}
                        placeholder="e.g., MyNamespace.MyClass"
                        maxLength={500}
                      />
                    </div>

                    <div className="form-group full">
                      <label>Method Name</label>
                      <input
                        type="text"
                        value={formData.MethodName}
                        onChange={(e) => handleChange('MethodName', e.target.value)}
                        disabled={!isEditing}
                        placeholder="e.g., Execute"
                        maxLength={200}
                      />
                    </div>
                  </div>
                )}

                {/* Parameters Tab */}
                {activeTab === 'parameters' && (
                  <EntityGrid
                    title="Function Parameters"
                    entities={parameters}
                    columns={parameterColumns}
                    onAdd={handleAddParameter}
                    onEdit={handleEditParameter}
                    onDelete={handleDeleteParameter}
                    loading={false}
                    emptyMessage="No parameters defined. Click 'Add' to define a parameter."
                    gridColumns={2}
                    idField="ParameterID"
                  />
                )}

                {/* Knowledge Bases Tab */}
                {activeTab === 'knowledgeBases' && (
                  <>
                    {!functionId ? (
                      <div className="form-grid">
                        <div className="form-group full">
                          <div className="display-value">
                            Save the function first before adding knowledge bases.
                          </div>
                        </div>
                      </div>
                    ) : (
                      <EntityGrid
                        title="Knowledge Bases"
                        entities={knowledgeBases}
                        columns={knowledgeBaseColumns}
                        onAdd={handleAddKnowledgeBase}
                        onEdit={handleEditKnowledgeBase}
                        onDelete={handleDeleteKnowledgeBase}
                        loading={knowledgeBasesLoading}
                        emptyMessage="No knowledge bases defined. Click 'Add' to associate one."
                        gridColumns={2}
                        idField="AgentFunctionKnowledgeBaseID"
                      />
                    )}
                  </>
                )}

                {/* Security Tab */}
                {activeTab === 'security' && (
                  <div className="form-grid read-only">
                    <div className="form-group">
                      <label>Primary ID</label>
                      <div className="display-value">{formData.AgentFunctionID || 'N/A'}</div>
                    </div>

                    <div className="form-group">
                      <label>Resource ID (ResID)</label>
                      <div className="display-value">{formData.ResID || 'N/A'}</div>
                    </div>

                    <div className="form-group">
                      <label>Created On</label>
                      <div className="display-value">
                        {formData.CreatedOn ? new Date(formData.CreatedOn).toLocaleString() : 'N/A'}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Created By</label>
                      <div className="display-value">{formData.CreatedBy || 'N/A'}</div>
                    </div>

                    <div className="form-group">
                      <label>Last Modified On</label>
                      <div className="display-value">
                        {formData.LastModifiedOn ? new Date(formData.LastModifiedOn).toLocaleString() : 'N/A'}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Last Modified By</label>
                      <div className="display-value">{formData.LastModifiedBy || 'N/A'}</div>
                    </div>
                  </div>
                )}

                {/* Debugger Tab */}
                {activeTab === 'debugger' && (
                  <div style={{ padding: '20px' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <h4 style={{ margin: '0 0 12px 0', color: '#f8fafc', fontSize: '14px' }}><ClipboardIcon size={12} /> Entity Data (JSON)</h4>
                      <textarea
                        value={JSON.stringify(formData, null, 2)}
                        readOnly
                        style={{
                          width: '100%',
                          height: '400px',
                          padding: '12px',
                          background: 'rgba(0,0,0,0.4)',
                          border: '1px solid rgba(58, 140, 69, 0.3)',
                          borderRadius: '6px',
                          color: '#3A8C45',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          resize: 'vertical',
                          cursor: 'default',
                        }}
                      />
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                      <LightbulbIcon size={12} /> Tip: Copy this JSON data for debugging or API requests
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="editor-footer">
                {isEditing ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setIsEditing(false);
                        resetForm();
                        if (functionId) loadFunctionData();
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                  </>
                ) : (
                  <>
                    {functionId && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleRefresh}
                        disabled={loading}
                        title="Refresh data from server"
                      >
                        <ZapIcon size={14} /> Refresh
                      </button>
                    )}
                    {functionId && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setIsEditing(true)}
                        title="Enable edit mode"
                      >
                        Edit
                      </button>
                    )}
                  </>
                )}
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Parameter Editor Modal */}
      {parameterEditorOpen && (
        <ParameterEditor
          isOpen={parameterEditorOpen}
          parameter={selectedParameter || undefined}
          onClose={() => {
            setParameterEditorOpen(false);
            setSelectedParameter(null);
          }}
          onParameterSaved={handleParameterSaved}
        />
      )}

      {/* Knowledge Base Editor Modal */}
      {knowledgeBaseEditorOpen && functionId && (
        <KnowledgeBaseEditor
          isOpen={knowledgeBaseEditorOpen}
          agentFunctionId={functionId}
          knowledgeBase={selectedKnowledgeBase || undefined}
          onClose={() => {
            setKnowledgeBaseEditorOpen(false);
            setSelectedKnowledgeBase(null);
          }}
          onSaved={handleKnowledgeBaseSaved}
        />
      )}
    </ModalPortal>
  );
};

export default AIFunctionEditor;
